import { useState } from 'react';
import { loadStripe, StripeElementsOptions, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { LogoLoader } from '@/components/ui/logo-loader';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';

// Lazy initialization: only load Stripe when key is present and component is used
let stripePromise: Promise<Stripe | null> | null = null;

function getStripePromise(): Promise<Stripe | null> | null {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  
  // Only initialize if key is present and non-empty
  if (!publishableKey || publishableKey.trim() === '') {
    return null;
  }
  
  // Initialize lazily (only when first needed)
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  
  return stripePromise;
}

interface StripePaymentFormProps {
  clientSecret: string;
  paymentIntentId?: string;
  onSuccess: (paymentIntentId: string) => void;
  onError: (error: string) => void;
  isLoading?: boolean;
}

function PaymentForm({ clientSecret, paymentIntentId, onSuccess, onError, isLoading: externalLoading }: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation(['common']);
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLoading = externalLoading || isProcessing;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Payment form validation failed');
        setIsProcessing(false);
        return;
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/settings?subscription=success`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment confirmation failed');
        onError(confirmError.message || 'Payment confirmation failed');
      } else {
        // Payment succeeded - use provided payment intent ID or extract from client secret
        // Client secret format: pi_xxx_secret_xxx, payment intent ID is the part before _secret_
        const intentId = paymentIntentId || clientSecret.split('_secret_')[0] || '';
        onSuccess(intentId);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      onError(errorMessage);
      toast({
        title: t('common:errors.paymentFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement 
        options={{
          layout: 'tabs',
        }}
      />
      
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || !elements || isLoading}
        className="w-full"
      >
        {isLoading ? (
          <LogoLoader size="sm" />
        ) : (
          t('common:buttons.payNow', 'Pay Now')
        )}
      </Button>
    </form>
  );
}

export function StripePaymentForm(props: StripePaymentFormProps) {
  const { clientSecret } = props;
  const { t } = useTranslation(['pages.settings']);

  if (!clientSecret) {
    return (
      <div className="p-4 text-center text-sm text-gray-500">
        Loading payment form...
      </div>
    );
  }

  // Get Stripe promise (lazy initialization)
  const stripePromise = getStripePromise();
  
  // If Stripe key is not configured, show error
  if (!stripePromise) {
    return (
      <div className="p-4 text-center text-sm text-red-600 bg-red-50 rounded-lg border border-red-200">
        {t('sections.subscription.errors.stripeNotConfigured')}
      </div>
    );
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#4A90E2',
        colorBackground: '#ffffff',
        colorText: '#333333',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm {...props} />
    </Elements>
  );
}

